import { useEffect } from "react";
import { create } from "zustand";
import { getActiveDesignFirmId, useWorkspaceStore } from "ui";
import type { StudioLibraryItemInterface } from "ts-interfaces";
import {
  addToStudioLibrary,
  getStudioLibraryDocs,
  getStudioLibraryItems,
  recordStudioLibraryUse,
  removeFromStudioLibrary,
  sortStudioItems,
  STUDIO_LIBRARY_PAGE_LIMIT,
  studioLibraryDocId,
  studioLibraryFields,
  type StudioLibraryRef,
} from "../v2/apis/studioLibraryApi";

/**
 * Studio Library list for the active workspace. Writes are optimistic (the
 * panel re-sorts the moment a tile is placed) and then persisted; a failed
 * persist is logged, never surfaced. Every mount re-reads it (teammates and
 * other tabs change it too); a read that a local write overtook is dropped.
 */
interface StudioLibraryState {
  items: StudioLibraryItemInterface[];
  loading: boolean;
  loadedKey: string | null;
  /** The load returned every row (under the page cap), so a missing row is a new product. */
  complete: boolean;
  load: (uid: string | undefined) => Promise<void>;
  /** `firmId`: the open board's workspace when known (else the active one). */
  bump: (
    uid: string,
    ref: StudioLibraryRef,
    firmId?: string | null,
  ) => Promise<void>;
  addMany: (
    uid: string,
    refs: StudioLibraryRef[],
  ) => Promise<{ added: number; duplicates: number }>;
  remove: (ids: string[]) => Promise<void>;
}

const studioLibraryKey = (uid: string): string =>
  `${uid}|${getActiveDesignFirmId(uid) ?? ""}`;

let inflight: { key: string; promise: Promise<void> } | null = null;
// Bumped by every local write. A refresh that resolves after one may predate
// it, so it's dropped rather than undoing the write; the next mount reads again.
let writeSeq = 0;

// The display fields to write: the stored row's when known (so a weaker drop
// payload never overwrites them, and a doc deleted elsewhere comes back whole),
// else the ref's.
const displayRef = (
  ref: StudioLibraryRef,
  known: StudioLibraryItemInterface | undefined,
): StudioLibraryRef =>
  known
    ? {
        ...ref,
        name: known.name || ref.name,
        supplier: known.supplier ?? ref.supplier,
        thumbnail: known.thumbnail ?? ref.thumbnail,
        productType: known.productType ?? ref.productType,
      }
    : ref;

const rowFor = (
  uid: string,
  firmId: string | null,
  ref: StudioLibraryRef,
  useCount: number,
  lastUsedAt: number,
): StudioLibraryItemInterface => ({
  ...studioLibraryFields(uid, firmId, ref),
  name: ref.name ?? "",
  useCount,
  lastUsedAt,
});

export const useStudioLibrary = create<StudioLibraryState>((set, get) => ({
  items: [],
  loading: false,
  loadedKey: null,
  complete: false,

  load: async (uid) => {
    if (!uid) {
      set({ items: [], loading: false, loadedKey: null, complete: false });
      return;
    }
    const key = studioLibraryKey(uid);
    if (inflight?.key === key) return inflight.promise;
    // Already showing this workspace: refresh in the background.
    const refresh = get().loadedKey === key;
    if (!refresh) set({ items: [], loading: true, complete: false });
    const seqAtStart = writeSeq;
    const firmId = getActiveDesignFirmId(uid);
    const promise = getStudioLibraryItems(uid, firmId)
      .then((items) => {
        // A slower load for a workspace the user has since left must not
        // overwrite the current one.
        if (studioLibraryKey(uid) !== key) return;
        if (refresh && writeSeq !== seqAtStart) return;
        set({
          items,
          loading: false,
          loadedKey: key,
          complete: items.length < STUDIO_LIBRARY_PAGE_LIMIT,
        });
      })
      .catch((e) => {
        console.error("Error loading studio library:", e);
        if (studioLibraryKey(uid) !== key) return;
        // The next mount reads again; until then nothing is known to be missing.
        set({ loading: false, loadedKey: key, complete: false });
      })
      .finally(() => {
        if (inflight?.key === key) inflight = null;
      });
    inflight = { key, promise };
    return promise;
  },

  bump: async (uid, ref, boardFirmId) => {
    const activeFirmId = getActiveDesignFirmId(uid);
    const firmId = boardFirmId === undefined ? activeFirmId : boardFirmId;
    if (firmId !== activeFirmId) {
      // A board from another workspace (the active one was switched from
      // another tab): file it there, leaving the active workspace's list alone.
      const id = studioLibraryDocId(uid, firmId, ref.source, ref.productId);
      const stored = await getStudioLibraryDocs(uid, firmId, [id]).then(
        (docs) => docs[0],
        () => undefined,
      );
      await recordStudioLibraryUse(uid, firmId, displayRef(ref, stored));
      return;
    }
    // Know the workspace's rows first: the write's display fields come from
    // the stored row when there is one, and a bump that lands mid-load must
    // not be wiped by the load's set({ items }). load() runs once per
    // workspace and returns the in-flight promise, so this is one read.
    // A switch during the awaits leaves the list alone: the row is this key's.
    const key = studioLibraryKey(uid);
    const shows = () => get().loadedKey === key && studioLibraryKey(uid) === key;
    if (get().loadedKey !== key) await get().load(uid);
    const id = studioLibraryDocId(uid, firmId, ref.source, ref.productId);
    const local = shows() ? get().items.find((i) => i.id === id) : undefined;
    // Past the loaded page (or after a failed load) a row missing here may
    // still exist: read it, so its count carries on and its display fields
    // are kept. A failed read falls back to the ref.
    const stored =
      local || (shows() && get().complete)
        ? undefined
        : await getStudioLibraryDocs(uid, firmId, [id]).then(
            (docs) => docs[0],
            () => undefined,
          );
    const now = Date.now();
    writeSeq += 1;
    if (shows())
      set((s) => ({
        items: sortStudioItems(
          s.items.some((i) => i.id === id)
            ? s.items.map((i) =>
                i.id === id ? { ...i, useCount: i.useCount + 1, lastUsedAt: now } : i,
              )
            : [
                ...s.items,
                stored
                  ? { ...stored, useCount: stored.useCount + 1, lastUsedAt: now }
                  : rowFor(uid, firmId, ref, 1, now),
              ],
        ),
      }));
    await recordStudioLibraryUse(uid, firmId, displayRef(ref, local ?? stored));
  },

  // Duplicates are checked against Firestore for anything the store doesn't
  // hold: it may be unloaded (Library bar, product modal) or past the page cap,
  // and re-adding an existing doc would reset its lastUsedAt.
  addMany: async (uid, refs) => {
    const firmId = getActiveDesignFirmId(uid);
    // The rows belong to the workspace the add began in, even if the user
    // switches while it reads.
    const key = studioLibraryKey(uid);
    const shows = () => get().loadedKey === key && studioLibraryKey(uid) === key;
    const byId = new Map(
      refs.map((r) => [studioLibraryDocId(uid, firmId, r.source, r.productId), r]),
    );
    const have = new Set(get().items.map((i) => i.id));
    const unknown = [...byId.keys()].filter((id) => !have.has(id));
    const found = new Set(
      unknown.length > 0
        ? (await getStudioLibraryDocs(uid, firmId, unknown)).map((d) => d.id)
        : [],
    );
    // Checked against the items again after the await: an overlapping call
    // (a double click) may have added the same product meanwhile.
    const haveNow = new Set(get().items.map((i) => i.id));
    const fresh = [...byId]
      .filter(([id]) => !haveNow.has(id) && !found.has(id))
      .map(([, r]) => r);
    if (fresh.length > 0) {
      const now = Date.now();
      const docId = (r: StudioLibraryRef) =>
        studioLibraryDocId(uid, firmId, r.source, r.productId);
      // The store may hold another workspace (no list mounted since a
      // switch): then only Firestore is written, and this workspace's next
      // load reads it.
      if (shows()) {
        writeSeq += 1;
        set((s) => ({
          items: sortStudioItems([
            ...s.items,
            ...fresh.map((r) => rowFor(uid, firmId, r, 0, now)),
          ]),
        }));
      }
      const committed = new Set<string>();
      try {
        await addToStudioLibrary(uid, firmId, fresh, (refs) =>
          refs.forEach((r) => committed.add(docId(r))),
        );
      } catch (e) {
        // Drop the rows that never landed, or a retry would skip them as duplicates.
        const failed = new Set(
          fresh.map(docId).filter((id) => !committed.has(id)),
        );
        if (shows())
          set((s) => ({ items: s.items.filter((i) => !failed.has(i.id)) }));
        throw e;
      }
    }
    return { added: fresh.length, duplicates: refs.length - fresh.length };
  },

  // Optimistic; a failed delete puts back the rows whose batch didn't commit
  // and rethrows for the caller to report.
  remove: async (ids) => {
    const drop = new Set(ids);
    const removed = get().items.filter((i) => drop.has(i.id));
    writeSeq += 1;
    set((s) => ({ items: s.items.filter((i) => !drop.has(i.id)) }));
    const committed = new Set<string>();
    try {
      await removeFromStudioLibrary(ids, (done) =>
        done.forEach((id) => committed.add(id)),
      );
    } catch (e) {
      const restore = removed.filter((i) => !committed.has(i.id));
      set((s) => ({
        items: sortStudioItems([
          ...s.items.filter((i) => !drop.has(i.id)),
          ...restore,
        ]),
      }));
      throw e;
    }
  },
}));

/**
 * Keeps the store loaded for `uid`'s active workspace: loads on mount and
 * again when the workspace flips (personal ↔ team). `enabled: false` leaves
 * the store alone entirely (a page that merely MAY enter add mode must not
 * clear the list other surfaces dedupe against).
 */
export const useStudioLibraryItems = (
  uid: string | undefined,
  enabled: boolean = true,
) => {
  const activeIdByUid = useWorkspaceStore((s) => s.activeIdByUid);
  const load = useStudioLibrary((s) => s.load);
  useEffect(() => {
    if (!enabled) return;
    void load(uid);
  }, [uid, enabled, activeIdByUid, load]);
  const items = useStudioLibrary((s) => s.items);
  const loading = useStudioLibrary((s) => s.loading);
  const loadedKey = useStudioLibrary((s) => s.loadedKey);
  // Until this workspace's rows arrive (the first render, a switch), the
  // store holds nothing for it: report loading, never an empty library or
  // another workspace's rows.
  const pending = enabled && !!uid && loadedKey !== studioLibraryKey(uid);
  return { items: pending ? NO_ITEMS : items, loading: loading || pending };
};

const NO_ITEMS: StudioLibraryItemInterface[] = [];
